import React, { useState } from 'react';
import { Alert, Button, Modal, ModalDialog, Stack, Typography } from '@mui/joy';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminOnboardingAPI } from '../../../lib/apiAdmin';
import { getApiErrorMessage } from '../../../lib/apiError';
import type { StaffTask } from '../../../types/admin';
export function DeleteTaskButton({
  task,
  disabled,
}: {
  task: StaffTask;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const cache = useQueryClient();
  const remove = useMutation({
    mutationFn: () => AdminOnboardingAPI.deleteTask(task.id),
    onSuccess: () => {
      setOpen(false);
      cache.invalidateQueries({ queryKey: ['admin'] });
      cache.invalidateQueries({ queryKey: ['admin-orgs'] });
      cache.invalidateQueries({ queryKey: ['admin-org'] });
    },
  });
  return (
    <>
      <Button
        size="sm"
        variant="plain"
        color="danger"
        disabled={disabled}
        onClick={() => setOpen(true)}
        aria-label={`Delete task: ${task.title}`}
      >
        Delete
      </Button>
      <Modal open={open} onClose={() => !remove.isPending && setOpen(false)}>
        <ModalDialog
          role="alertdialog"
          aria-labelledby={`delete-task-${task.id}`}
        >
          <Typography id={`delete-task-${task.id}`} level="h4">
            Delete this task?
          </Typography>
          <Typography>{task.title}</Typography>
          <Typography level="body-sm">
            Remove it from the team queue without marking it complete. Automatic
            registration will not recreate it.
          </Typography>
          {remove.error && (
            <Alert color="danger">
              {getApiErrorMessage(remove.error, 'Could not delete the task.')}
            </Alert>
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              disabled={remove.isPending}
              onClick={() => setOpen(false)}
            >
              Keep task
            </Button>
            <Button
              color="danger"
              loading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Delete task
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </>
  );
}
